import React, { useCallback, useMemo } from 'react';
import { DownloadIcon, ChevronDownIcon, RefreshIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { Menu } from '@headlessui/react';
import { useDownloadTotalSalesReport } from '../../hooks/useAnalyticsApi';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

// Optimized ExportButtons component with memoization
const ExportButtons = React.memo(({ projectId, apiDateParams }) => {
	const downloadTotalSalesReportMutation = useDownloadTotalSalesReport({
		onSuccess: useCallback((response) => {
			if (response && response.data && typeof response.data === 'string') {
				const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
				saveAs(blob, `total-sales-report-${apiDateParams.start_date}-to-${apiDateParams.end_date}.csv`);
				toast.success(response.message || 'Report downloaded successfully!');
			} else {
				toast.error('Failed to download report: Invalid data format.');
				console.error('Invalid data format for CSV download:', response);
			}
		}, [apiDateParams.start_date, apiDateParams.end_date]),
		onError: useCallback((error) => {
			toast.error(error.message || 'Failed to download report.');
			console.error('Error downloading total sales report:', error);
		}, []),
	});

	const handleDownloadTotalSales = useCallback(() => {
		if (!projectId) {
			toast.error('Project ID is missing.');
			return;
		}
		if (!apiDateParams || !apiDateParams.start_date || !apiDateParams.end_date) {
			toast.error('Date range is missing.');
			return;
		}
		const queryParams = {
			start_date: apiDateParams.start_date,
			end_date: apiDateParams.end_date,
			store_url: apiDateParams.store_url,
		};
		downloadTotalSalesReportMutation.mutate({ projectId, queryParams });
	}, [projectId, apiDateParams, downloadTotalSalesReportMutation]);

	const pastMonths = useMemo(() => {
		const months = [];
		for (let i = 1; i <= 3; i++) {
			const date = dayjs().subtract(i, 'month');
			months.push(date.format('MMMM YYYY'));
		}
		return months;
	}, []);

	return (
		<div className="flex flex-row gap-2">
			<button
				onClick={handleDownloadTotalSales}
				disabled={downloadTotalSalesReportMutation.isLoading}
				className="border border-blue-500 text-blue-500 text-sm px-4 py-2 rounded-md flex flex-row gap-2 items-center disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{downloadTotalSalesReportMutation.isLoading ? (
					<RefreshIcon className="w-4 h-4 animate-spin" />
				) : (
					<DownloadIcon className="w-4 h-4" />
				)}
				<span>{downloadTotalSalesReportMutation.isLoading ? 'Downloading...' : 'Download Report'}</span>
			</button>
			<Menu as="div" className="relative inline-block text-left">
				<Menu.Button className="border border-blue-500 text-blue-500 text-sm px-4 py-2 rounded-md flex flex-row gap-2 items-center">
					<span>Export Order Details</span>
					<ChevronDownIcon className="w-4 h-4" />
				</Menu.Button>
				<Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg z-10 py-2">
					{pastMonths.map((month, index) => (
						<Menu.Item key={index}>
							<button className="text-sm w-full p-2 text-black hover:bg-gray-50">
								{month}
							</button>
						</Menu.Item>
					))}
				</Menu.Items>
			</Menu>
		</div>
	);
});

ExportButtons.displayName = 'ExportButtons';

export default ExportButtons; 